'use client';
import React from 'react';
import { useRouter } from 'next/navigation';
import { Clock, Zap } from 'lucide-react';
import { DEALS } from '../data/deals';

const DEAL_TO_PROPERTY: Record<string, string> = {
  'deal-1': 'prop-1',
  'deal-2': 'prop-2',
  'deal-3': 'prop-4',
  'deal-4': 'prop-3',
};

export default function DealsSection() {
  const router = useRouter();

  return (
    <section className="py-10 sm:py-14 bg-white">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        {/* Header */}
        <div className="flex items-end justify-between mb-5 sm:mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Zap size={14} className="text-red-500 fill-red-500" />
              <p className="text-xs font-display font-600 text-red-500 uppercase tracking-widest" style={{ fontWeight: 600 }}>
                Limited Time
              </p>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-800 text-gray-900" style={{ fontWeight: 800 }}>
              Today's Best Deals
            </h2>
          </div>
          <button
            onClick={() => router?.push('/hotels')}
            className="text-sm font-display font-600 text-primary-500 hover:text-primary-600 transition-colors duration-150"
            style={{ fontWeight: 600 }}>
            All deals →
          </button>
        </div>

        {/* Mobile: horizontal scroll, Desktop: grid */}
        <div className="flex gap-3 overflow-x-auto pb-2 sm:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4 scrollbar-hide">
          {DEALS?.map((deal) => (
            <div
              key={deal?.id}
              onClick={() => router?.push('/hotels')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && router?.push('/hotels')}
              aria-label={`${deal?.title} - ${deal?.discount} off`}
              className="group relative bg-white rounded-2xl overflow-hidden card-shadow hover:card-shadow-lg transition-all duration-300 cursor-pointer border border-gray-100 hover:border-primary-200 flex-shrink-0 w-56 sm:w-auto active:scale-[0.98]"
            >
              {/* Image */}
              <div className="relative h-36 sm:h-44 overflow-hidden">
                <img
                  src={deal?.image}
                  alt={deal?.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-display font-700 text-white ${deal?.badgeColor}`} style={{ fontWeight: 700 }}>
                    {deal?.badge}
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-xs font-display font-700 text-white bg-black/70 backdrop-blur-sm" style={{ fontWeight: 700 }}>
                    {deal?.discount}
                  </span>
                </div>
                <div className="absolute bottom-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur-sm">
                  <Clock size={11} className="text-yellow-400" />
                  <span className="text-xs font-display font-600 text-yellow-400" style={{ fontWeight: 600 }}>
                    {deal?.expiresIn}
                  </span>
                </div>
              </div>

              {/* Info */}
              <div className="p-3 sm:p-4">
                <h3 className="font-display font-700 text-gray-900 text-xs sm:text-sm leading-snug mb-0.5" style={{ fontWeight: 700 }}>
                  {deal?.title}
                </h3>
                <p className="text-xs text-gray-500 font-body mb-2 sm:mb-3 line-clamp-1">{deal?.subtitle}</p>
                <div className="flex items-end justify-between gap-2">
                  <div className="min-w-0">
                    <span className="text-xs text-gray-400 font-body line-through block">
                      {deal?.originalPrice?.toLocaleString()} {deal?.currency}
                    </span>
                    <span className="font-display font-800 text-primary-500 text-sm sm:text-lg leading-tight" style={{ fontWeight: 800 }}>
                      {deal?.salePrice?.toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-400 font-body ml-1 hidden sm:inline">{deal?.currency}/night</span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); router?.push('/hotels'); }}
                    className="px-2.5 sm:px-3 py-2 rounded-lg bg-primary-500 text-white text-xs font-display font-600 hover:bg-primary-600 transition-colors duration-150 min-h-[36px] active:scale-95 flex-shrink-0"
                    style={{ fontWeight: 600 }}>
                    Book
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
